import React, { useCallback, useEffect, useState } from 'react';
import update from 'immutability-helper';
import FontAwesomeIcon from '@fortawesome/react-fontawesome';
import {faChevronDown, faChevronRight, faArrowUp, faArrowDown, faPlus, faClock} from '@fortawesome/fontawesome-free-solid/';
import i18next from 'i18next';

import ChecklistTaskStep from './checklist_task_step';
import ChecklistCorrectiveAction from './checklist_corrective_action';
import { TextInput } from '../TextInput';

import config from "config";
import ChecklistTaskTimer from './checklist_task_timer';

const ChecklistTaskHeader = ({ task, readOnly, expanded, onTaskMoveUp, onTaskMoveDown, onTaskCopy, onTaskDelete, onExpandedChange, onShowTimerArea }) => {
    const chevron = expanded ? faChevronDown : faChevronRight;
    const taskName = `${i18next.t('pages.checklists.task')} ${expanded ? '' : `: ${task.name}`}`;

    return (
        <div className="row checklist-header-title">
            <ul className="list-inline">
                { onExpandedChange &&
                <li className="pull-left"><button className="btn btn-basic" onClick={() => onExpandedChange(!expanded)}><FontAwesomeIcon icon={chevron}/></button></li>
                }

                { !readOnly && onTaskMoveUp &&
                <li className="pull-left"><button className="btn btn-basic" onClick={() => onTaskMoveUp(task)}><FontAwesomeIcon icon={faArrowUp}/></button></li>
                }

                { !readOnly &&onTaskMoveDown &&
                <li className="pull-left"><button className="btn btn-basic" onClick={() => onTaskMoveDown(task)}><FontAwesomeIcon icon={faArrowDown}/></button></li>
                }
                
                <li><h4>{taskName}</h4></li>

                { !readOnly && onTaskDelete &&
                <li className="pull-right"><button className="btn btn-basic" onClick={() => onTaskDelete(task)}><img src={`${config.WEB_BASE_URL}/images/icon/delete-trash-can.svg`} name="delete" alt={i18next.t('delete')}/></button></li>
                }
                
                { !readOnly && onTaskCopy &&
                <li className="pull-right"><button className="btn btn-basic" onClick={() => onTaskCopy(task)}><img src={`${config.WEB_BASE_URL}/images/icon/copy.svg`} name="copy" alt={i18next.t('copy')} /></button></li>
                }

                { ((!readOnly && onShowTimerArea) || (readOnly && task.checklistTaskTimerConfig != null)) &&
                <li className="pull-right">
                    <button className="btn btn-basic" onClick={() => onShowTimerArea()}>
                        <FontAwesomeIcon className="force-fa-size" icon={faClock} style={{color: task.checklistTaskTimerConfig ? "#a4deb5" : "white"}} />
                    </button>
                </li>
                }
            </ul>
        </div>
    );
};

const StepMenuItem = ({ isSelected, name, onSelection }) => (
    <div>
        {
            isSelected ?
            <div className="row op-checklist-step-title">
                <button className="btn btn-block checklist-step-selected" onClick={onSelection}>
                    <div>
                        <span className="pull-left" style={{fontWeight: 'bold'}}>{name}</span>
                        <div className="pull-right"><FontAwesomeIcon icon={faChevronRight} /></div>
                    </div>
                </button>
            </div>:
            <div className="row op-checklist-step-title">
                <button className="btn btn-basic btn-block checklist-step-unselected" onClick={onSelection}>
                    <div>
                        <span className="pull-left">{name}</span>
                    </div>
                </button>
            </div>
        }
    </div>
);

const ChecklistTask = ({ task, readOnly, expanded, stepTypes, checklistDeviceTags, onTaskUpdate, onTaskMoveUp, onTaskMoveDown, onTaskCopy, onTaskDelete }) => {
    const [selectedStep, setSelectedStep] = useState(task.checklistTaskOptionConfigs?.length ? task.checklistTaskOptionConfigs[0] : null);
    const [correctiveActionContext, setCorrectiveActionContext] = useState(null);
    const [showTimerArea, setShowTimerArea] = useState(false);
    const [taskExpanded, setTaskExpanded] = useState(expanded);

    useEffect(() => {
        setTaskExpanded(expanded);
    }, [expanded]);

    useEffect(() => {
        if (task.checklistTaskOptionConfigs?.length > 0) {
            const firstStepWithErrors = task.checklistTaskOptionConfigs.find((step) => step.errors && Object.keys(step.errors).length > 0);
            if (firstStepWithErrors) {
                setSelectedStep(firstStepWithErrors);
            }
        } else {
            setSelectedStep(null);
        }
    }, [task]);

    const updateTaskName = useCallback((name) => { 
        onTaskUpdate({ ...task, name: name }); 
    }, [task, onTaskUpdate]);

    const updateTaskDescription = useCallback((description) => { 
        onTaskUpdate({ ...task, description: description }); 
    }, [task, onTaskUpdate]);

    const addTaskStep = useCallback(() => {
        let steps = task.checklistTaskOptionConfigs ? task.checklistTaskOptionConfigs : [];
        const newStep = { 
            uid: Math.floor(Math.random() * 99999 + 999000000), 
            name: "" 
        };
        steps.push(newStep);

        setSelectedStep(newStep);

        onTaskUpdate(update(task, { checklistTaskOptionConfigs: { $set: steps } }));
    }, [task, onTaskUpdate]);

    const updateTaskStep = useCallback((stepToUpdate) => {
        const updatedStep = { ...stepToUpdate, errors: {} };
        const updatedSteps = task.checklistTaskOptionConfigs.map((step) => (step.uid === updatedStep.uid) ? updatedStep : step);

        setSelectedStep( updatedStep );

        onTaskUpdate(update(task, { checklistTaskOptionConfigs: { $set: updatedSteps } }));
    }, [task, onTaskUpdate]);

    const removeTaskStep = useCallback((stepToRemove) => {
        let steps = [...task.checklistTaskOptionConfigs];
        steps.splice(steps.indexOf(stepToRemove), 1);

        setSelectedStep(steps.length > 0 ? steps[0] : null);

        onTaskUpdate(update(task, { checklistTaskOptionConfigs: { $set: steps } }));
    }, [task, onTaskUpdate]);

    const updateCorrectiveAction = useCallback((correctiveActionContext) => {
        const updatedStep = correctiveActionContext.ownerStepUpdateFunction(correctiveActionContext.correctiveAction);

        onTaskUpdate(update(task, {
            checklistTaskOptionConfigs: {
                $set: task.checklistTaskOptionConfigs.map((step) => (step.uid === updatedStep.uid ? updatedStep : step)),
            },
        }));
        
        if (updatedStep.uid === selectedStep?.uid) {
            setSelectedStep(updatedStep);
        }
        
        setCorrectiveActionContext(correctiveActionContext);
    }, [task, onTaskUpdate]);

    const showCorrectiveAction = useCallback((correctiveActionContext) => {
        const currentStepMatch = task.checklistTaskOptionConfigs.find((step) => (step.uid === selectedStep?.uid));
        if (currentStepMatch) {
            setSelectedStep(currentStepMatch);
        }

        setCorrectiveActionContext(correctiveActionContext);
    }, [task]);

    const dismissCorrectiveAction = useCallback(() => {
        if (correctiveActionContext?.correctiveAction?.checklistTaskOptionConfigs) {
            let containsErrors = false;

            let updatedChecklistTaskOptionConfigs = correctiveActionContext.correctiveAction.checklistTaskOptionConfigs.map((step) => {
                let containsStepErrors = false;
                let updatedStep = { ...step, errors: {} };

                if (!step.type) {
                    containsStepErrors = true;
                    updatedStep.errors.type = i18next.t("messages.please-select-step-type");
                }

                if (!step.name || step.name === "") {
                    containsStepErrors = true;
                    updatedStep.errors.name = i18next.t("messages.please-select-step-name");
                }

                if (step.checklistTaskOptionType && step.type === "selection") {
                    if (step.checklistTaskSelectionOptions) {
                        let containsSelectionErrors = false;
                        const updatedChecklistTaskSelectionOptions = step.checklistTaskSelectionOptions.map((selectOption) => {
                            if (!selectOption.name) {
                                let updatedSelectOption = { ...selectOption, errors: {} };
                                containsSelectionErrors = true;
                                updatedSelectOption.errors.name = i18next.t("messages.please-enter-selection-name");
                                return updatedSelectOption;
                            } else {
                                return selectOption;
                            }
                        });
                        if (containsSelectionErrors) {
                            updatedStep.checklistTaskSelectionOptions = updatedChecklistTaskSelectionOptions;
                            containsStepErrors = true;
                        }
                    }
                }

                if (containsStepErrors) {
                    containsErrors = true;
                    return updatedStep;
                } else {
                    return step;
                }
            });

            if (containsErrors) {
                setCorrectiveActionContext({
                    ...correctiveActionContext,
                    correctiveAction: {
                        ...correctiveActionContext.correctiveAction,
                        checklistTaskOptionConfigs: updatedChecklistTaskOptionConfigs,
                    },
                });
            } else {
                setCorrectiveActionContext(null);
            }
        } else {
            setCorrectiveActionContext(null);
        }
    }, [task]);

    const updateTimer = useCallback((updatedTimer) => {
        onTaskUpdate({ ...task, checklistTaskTimerConfig: updatedTimer });
    }, [task, onTaskUpdate]);
    
    return taskExpanded ? (
        <div className="row checklist-config checklist-padding">
            <div className="col-sm-12">
                <div className="col-sm-4">
                    <div className="row checklist-config-task-container">
                        <ChecklistTaskHeader
                            task={task}
                            readOnly={readOnly}
                            expanded={taskExpanded}
                            onTaskMoveUp={onTaskMoveUp}
                            onTaskMoveDown={onTaskMoveDown}
                            onTaskCopy={onTaskCopy}
                            onTaskDelete={onTaskDelete}
                            onExpandedChange={setTaskExpanded}
                            onShowTimerArea={showTimerArea ? null : () => setShowTimerArea(!showTimerArea)}
                        />

                        <div className="checklist-padding">
                            <TextInput
                                input={{
                                    value: task.name ? task.name : "",
                                    onChange: (event) => updateTaskName(event.target.value),
                                }}
                                type="text"
                                label={i18next.t("form-labels.task-name")}
                                readOnly={readOnly}
                                meta={task.errors && task.errors.name ? { touched: true, error: task.errors.name } : {}}
                            />
                            <TextInput
                                input={{
                                    value: task.description ? task.description : "",
                                    onChange: (event) => updateTaskDescription(event.target.value),
                                }}
                                type="text"
                                label={i18next.t("form-labels.description-optional")}
                                readOnly={readOnly}
                                meta={{}}
                            />
                        </div>
                    </div>
                </div>

                {showTimerArea ? (
                    <div className="col-sm-8">
                        <ChecklistTaskTimer readOnly={readOnly} originalTimerConfig={task.checklistTaskTimerConfig} onTimerUpdate={updateTimer} onDismiss={() => setShowTimerArea(false)} />
                    </div>
                ) : (
                    <div className="col-sm-8">
                        {correctiveActionContext ? (
                            <ChecklistCorrectiveAction
                                readOnly={readOnly}
                                stepTypes={stepTypes}
                                checklistDeviceTags={checklistDeviceTags}
                                correctiveActionContext={correctiveActionContext}
                                updateCorrectiveAction={updateCorrectiveAction}
                                onDismiss={dismissCorrectiveAction}
                            />
                        ) : (
                            <div className="row checklist-config-task-step-box">
                                <div className="row checklist-header-title">
                                    <ul className="list-inline clearfix">
                                        <li className="pull-left">
                                            <h4>{i18next.t("form-labels.steps")}</h4>
                                        </li>
                                        <li className="pull-right">
                                            {!readOnly && (
                                                <button className="btn btn-sm btn-default" onClick={addTaskStep}>
                                                    <FontAwesomeIcon icon={faPlus} /> {i18next.t("form-labels.step")}
                                                </button>
                                            )}
                                        </li>
                                    </ul>
                                </div>
                                <div className="row top-row">
                                    <div className="col-sm-4">
                                        {
                                            // Render the task step side menu
                                            task.checklistTaskOptionConfigs && (
                                                <div>
                                                    {task.checklistTaskOptionConfigs.map((step) => (
                                                        <StepMenuItem
                                                            key={`step-${step.uid}`}
                                                            name={step.name && step.name !== "" ? step.name : "----"}
                                                            isSelected={selectedStep && step.uid === selectedStep.uid}
                                                            onSelection={() => setSelectedStep(step)}
                                                        />
                                                    ))}
                                                </div>
                                            )
                                        }
                                    </div>

                                    <div className="col-sm-8 checklist-config-task-step-details-box" style={{ height: 600, overflow: "auto" }}>
                                        {selectedStep && (
                                            <ChecklistTaskStep
                                                stepTypes={stepTypes}
                                                checklistDeviceTags={checklistDeviceTags}
                                                step={selectedStep}
                                                readOnly={readOnly}
                                                onStepUpdate={updateTaskStep}
                                                onStepRemove={removeTaskStep}
                                                onCorrectiveAction={showCorrectiveAction}
                                                inCorrectiveAction={false}
                                            />
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    ) : (
        <div className="row checklist-padding">
            <div className="col-sm-12">
                <div className="checklist-config-task-container-collapsed">
                    <ChecklistTaskHeader
                        task={task}
                        readOnly={readOnly}
                        expanded={taskExpanded}
                        onTaskMoveUp={onTaskMoveUp}
                        onTaskMoveDown={onTaskMoveDown}
                        onTaskCopy={onTaskCopy}
                        onTaskDelete={onTaskDelete}
                        onExpandedChange={setTaskExpanded}
                    />
                </div>
            </div>
        </div>
    );
}

export default ChecklistTask;